import { useEffect, useState } from "react";
import { Check, Download, ShieldAlert } from "lucide-react";
import { api, errorText } from "../lib/api";
import { fmt, fmtDate } from "../lib/constants";
import { AUDIT_GROUPS, CHECK_FIX, INFO_LISTS, fixedTodayText, fmtDelta } from "../lib/hygiene";
import { HygieneIgnores } from "./HygieneIgnores";
import { HygieneTrend } from "./HygieneTrend";

const NOTE_MAX = 600;

const downloadCsv = async (key) => {
  const r = await api.get(`/admin/hygiene/export/${key}.csv`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `crm-hygiene-${key}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const NoteEditor = () => {
  const [value, setValue] = useState("");
  const [saved, setSaved] = useState("");
  const [state, setState] = useState("");
  useEffect(() => {
    api.get("/admin/hygiene/note").then((r) => {
      setValue(r.data.value || "");
      setSaved(r.data.value || "");
    }).catch(() => {});
  }, []);
  const save = async () => {
    setState("saving");
    try {
      const r = await api.put("/admin/hygiene/note", { value });
      setSaved(r.data.value);
      setValue(r.data.value);
      setState("saved");
      setTimeout(() => setState(""), 2000);
    } catch (e) {
      setState(errorText(e));
    }
  };
  return (
    <div className="panel p-4 space-y-2" data-testid="hygiene-note-editor">
      <div className="flex items-center justify-between gap-3">
        <div className="eyebrow">Team rules note · shown at the top of the hygiene panel</div>
        <span className="num text-xs text-slate-400">{value.length}/{NOTE_MAX}</span>
      </div>
      <textarea
        data-testid="hygiene-note-input"
        value={value}
        maxLength={NOTE_MAX}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        placeholder="e.g. Every Won deal needs AM Assigned + Lead Source before the handoff. Duplicates: keep the oldest deal, merge the rest."
        className="w-full rounded-lg bg-slate-50 border border-slate-300 px-3 py-2 text-sm text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
      />
      <div className="flex items-center gap-3">
        <button type="button" data-testid="hygiene-note-save" onClick={save} disabled={value === saved || state === "saving"} className="inline-flex items-center gap-2 rounded-full bg-ink text-page disabled:opacity-40 px-4 py-2 text-xs font-semibold hover:bg-ink/85 transition-colors">
          {state === "saved" ? <Check className="w-3.5 h-3.5" /> : null} {state === "saving" ? "Saving…" : state === "saved" ? "Saved" : "Save note"}
        </button>
        {state && !["saving", "saved"].includes(state) && <span className="text-xs text-brand" data-testid="hygiene-note-error">{state}</span>}
        <span className="text-xs text-slate-500">Free text, max {NOTE_MAX} characters. Empty = no note on the board.</span>
      </div>
    </div>
  );
};

// Admin: CRM hygiene audit summary, per-check CSV exports and the free-text rules note
export const HygieneAdmin = ({ hygiene, countFrom }) => {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const dl = async (key) => {
    setBusy(key);
    setError("");
    try {
      await downloadCsv(key);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy("");
    }
  };
  const deltaText = fmtDelta(hygiene?.delta_yesterday);
  return (
    <section data-testid="hygiene-admin-section" className="space-y-3">
      <div>
        <div className="eyebrow flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5 text-brand" /> CRM hygiene · HubSpot audit since {fmtDate(countFrom)}</div>
        <p className="text-xs text-slate-500 mt-1 max-w-3xl" data-testid="hygiene-admin-help">
          Ten checks from the 10 Oct CRM audit plus “Won without line items”, recomputed on every refresh for deals and companies created since {fmtDate(countFrom)} (test records excluded; the line-items check covers every deal won since then, like the board). Counted per deal / per company. “Venue without a Merchant” applies only to venues with at least one deal (any pipeline or stage, created any time) – prospect venues without a deal are skipped until a deal is created, and dropping out of scope is never counted as “fixed”. The board shows the headline as a pill for signed-in people only – display screens never see it. A check whose HubSpot property cannot be found by its label is reported as “not available”, never as zero.
        </p>
      </div>
      {hygiene?.error && <div className="panel p-3 text-sm text-brand" data-testid="hygiene-admin-error">Last computation failed: {hygiene.error}</div>}
      {hygiene?.checks && (
        <div className="panel p-4 flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="hygiene-admin-summary">
          <div><span className="eyebrow block">Open issues</span><span className="num font-bold text-lg" data-testid="hygiene-admin-total">{fmt(hygiene.total)}</span></div>
          <div><span className="eyebrow block">Records</span><span className="num font-bold text-lg">{fmt(hygiene.records)}</span></div>
          <div><span className="eyebrow block">Since yesterday</span><span className={`num font-bold text-lg ${hygiene.delta_yesterday > 0 ? "text-brand" : hygiene.delta_yesterday < 0 ? "text-live" : ""}`}>{deltaText || "—"}</span></div>
          <div><span className="eyebrow block">Fixed · 7 days</span><span className="num font-bold text-lg text-live">{fmt(hygiene.fixed_7d || 0)}</span></div>
          <div><span className="eyebrow block">Fixed · today</span><span className="num font-bold text-lg text-live" data-testid="hygiene-admin-fixed-today" title={fixedTodayText(hygiene.fixed_today_by_check)}>{fmt(hygiene.fixed_today || 0)}</span></div>
          <div><span className="eyebrow block">Ignored</span><span className="num font-bold text-lg text-slate-500" data-testid="hygiene-admin-ignored">{fmt(hygiene.ignored?.count || 0)}</span></div>
          <div><span className="eyebrow block">Scanned</span><span className="num font-bold text-lg">{fmt(hygiene.scanned?.deals)} <span className="text-slate-400 text-xs font-normal">deals</span> · {fmt(hygiene.scanned?.companies)} <span className="text-slate-400 text-xs font-normal">companies</span></span></div>
          <div className="basis-full lg:basis-auto"><span className="eyebrow block mb-1">30-day trend</span><HygieneTrend trend={hygiene.trend} /></div>
          <div className="ml-auto self-end">
            <button type="button" data-testid="hygiene-csv-all" onClick={() => dl("all")} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-[#c42320] disabled:opacity-50 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors">
              <Download className="w-3.5 h-3.5" /> {busy === "all" ? "Preparing…" : "All findings · CSV"}
            </button>
          </div>
        </div>
      )}
      {error && <div className="panel p-3 text-sm text-brand" data-testid="hygiene-csv-error">{error}</div>}
      <div className="panel divide-y divide-panel-border" data-testid="hygiene-admin-checks">
        {!hygiene?.checks && <div className="p-4 text-sm text-slate-500">No hygiene data yet – wait for the next refresh.</div>}
        {(hygiene?.checks || []).map((c) => (
          <div key={c.key} data-testid={`hygiene-admin-check-${c.key}`} className="px-4 py-2.5 text-sm flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1">
            <span className="num text-xs text-slate-400 w-24 shrink-0 whitespace-nowrap" title={AUDIT_GROUPS[c.group]}>#{c.group} · {c.applies_to}</span>
            <span className="flex-1 min-w-[200px]">
              <span className="font-semibold text-ink">{c.label}</span>
              <span className="block text-xs text-slate-500">{CHECK_FIX[c.key]}</span>
            </span>
            <span className={`num font-bold w-20 text-right shrink-0 ${!c.available ? "text-slate-400 text-xs font-semibold" : c.count ? "text-brand" : "text-live"}`} data-testid={`hygiene-admin-count-${c.key}`}>
              {c.available ? fmt(c.count) : "not available"}
              {c.available && fmtDelta(c.delta_yesterday) && <span className={`block text-[10px] font-semibold ${c.delta_yesterday > 0 ? "text-brand" : c.delta_yesterday < 0 ? "text-live" : "text-slate-400"}`} data-testid={`hygiene-admin-delta-${c.key}`}>{fmtDelta(c.delta_yesterday)} vs yesterday</span>}
            </span>
            <button type="button" data-testid={`hygiene-csv-${c.key}`} onClick={() => dl(c.key)} disabled={!!busy || !c.available || !c.count} title={c.available ? "Download these findings as CSV" : "Property not found in HubSpot"} className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0">
              <Download className="w-3.5 h-3.5" /> {busy === c.key ? "…" : "CSV"}
            </button>
          </div>
        ))}
        {Object.values(INFO_LISTS).map((info) => hygiene?.[info.key] && (
          <div key={info.key} data-testid={`hygiene-admin-check-${info.key}`} className="px-4 py-2.5 text-sm flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 bg-slate-50/60">
            <span className="num text-xs text-slate-400 w-24 shrink-0 whitespace-nowrap" title="Informational – outside the total">info · deal</span>
            <span className="flex-1 min-w-[200px]">
              <span className="font-semibold text-slate-700">{info.label}</span>
              <span className="block text-xs text-slate-500">{info.help}</span>
            </span>
            <span className="num font-bold w-20 text-right shrink-0 text-slate-500" data-testid={`hygiene-admin-count-${info.key}`}>{fmt(hygiene[info.key].count)}</span>
            <button type="button" data-testid={`hygiene-csv-${info.key}`} onClick={() => dl(info.key)} disabled={!!busy || !hygiene[info.key].count} title="Download the list as CSV" className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0">
              <Download className="w-3.5 h-3.5" /> {busy === info.key ? "…" : "CSV"}
            </button>
          </div>
        ))}
      </div>
      <NoteEditor />
      <HygieneIgnores refreshKey={hygiene?.ignored?.count} />
    </section>
  );
};
